import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import AdminLayout from "../../components/AdminLayout";
import "./Gallery.css";

// ─── Snackbar ─────────────────────────────────────────────────────────────────
// Usage: const { showSnack } = useSnackbar();
//        showSnack("Something went wrong", "error");   // or "success"
function useSnackbar() {
  const [snack, setSnack] = useState(null); // { msg, type, id }
  const timerRef = useRef();

  const showSnack = useCallback((msg, type = "error") => {
    clearTimeout(timerRef.current);
    setSnack({ msg, type, id: Date.now() });
    timerRef.current = setTimeout(() => setSnack(null), 4500);
  }, []);

  const dismissSnack = useCallback(() => {
    clearTimeout(timerRef.current);
    setSnack(null);
  }, []);

  return { snack, showSnack, dismissSnack };
}

function Snackbar({ snack, onDismiss }) {
  if (!snack) return null;
  return (
    <div
      key={snack.id}
      className={`snackbar snackbar--${snack.type}`}
      role="alert"
      aria-live="assertive"
    >
      <span className="snackbar__icon">
        {snack.type === "error" ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <span className="snackbar__msg">{snack.msg}</span>
      <button className="snackbar__close" onClick={onDismiss} aria-label="Dismiss">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

// ─── Storage Bar ─────────────────────────────────────────────────────────────
function StorageBar({ stats }) {
  if (!stats) return null;

  const pct = Math.min(stats.percent, 100);
  const usedGB = stats.used_gb.toFixed(2);
  const totalGB = stats.total_gb.toFixed(0);
  const isFull = pct >= 100;
  const isWarn = pct >= 80;

  return (
    <div className="storage-bar">
      <div className="storage-bar__track">
        <div
          className={`storage-bar__fill${isWarn ? (isFull ? " storage-bar__fill--full" : " storage-bar__fill--warn") : ""}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="storage-bar__labels">
        <span className={`storage-bar__used${isFull ? " storage-bar__used--full" : ""}`}>
          {isFull ? "Storage full" : `${usedGB} GB used`}
        </span>
        <span className="storage-bar__total">{totalGB} GB limit</span>
      </div>
    </div>
  );
}
function CategoryDropdown({ categories, value, onChange, placeholder, multi = false, showAll = true }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = multi ? (Array.isArray(value) ? value : []) : value;
  const baseOptions = multi ? categories.map((c) => c.name) : (showAll ? ["All", ...categories.map((c) => c.name)] : categories.map((c) => c.name));
  const filtered = baseOptions.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
  const isChecked = (opt) => multi ? selected.includes(opt) : selected === opt;

  const toggle = (opt) => {
    if (!multi) { onChange(opt); setOpen(false); setSearch(""); return; }
    onChange(isChecked(opt) ? selected.filter((s) => s !== opt) : [...selected, opt]);
  };

  let displayLabel;
  if (multi) {
    displayLabel = selected.length === 0 ? (placeholder || "Select categories")
      : selected.length === 1 ? selected[0]
        : `${selected[0]} +${selected.length - 1} more`;
  } else {
    displayLabel = value || placeholder || (showAll ? "All" : "Select...");
  }
  const isEmpty = multi ? selected.length === 0 : !value && !!placeholder;

  return (
    <div className="cat-dropdown" ref={ref}>
      <button
        type="button"
        className={`cat-dropdown__trigger${open ? " cat-dropdown__trigger--open" : ""}${isEmpty ? " cat-dropdown__trigger--placeholder" : ""}`}
        onClick={() => setOpen((p) => !p)}
      >
        <span className="cat-dropdown__trigger-label">{displayLabel}</span>
        <svg className="cat-dropdown__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {multi && selected.length > 0 && (
        <div className="cat-dropdown__chips">
          {selected.map((s) => (
            <span key={s} className="cat-dropdown__chip">
              {s}
              <button type="button" className="cat-dropdown__chip-remove"
                onClick={(e) => { e.stopPropagation(); toggle(s); }} aria-label={`Remove ${s}`}>✕</button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="cat-dropdown__panel">
          <div className="cat-dropdown__search-wrap">
            <svg className="cat-dropdown__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input className="cat-dropdown__search" type="text" placeholder="Search category…"
              value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          </div>
          <ul className="cat-dropdown__list">
            {filtered.length === 0 ? (
              <li className="cat-dropdown__empty">No results</li>
            ) : filtered.map((opt) => (
              <li key={opt}
                className={`cat-dropdown__option${isChecked(opt) ? " cat-dropdown__option--active" : ""}`}
                onClick={() => toggle(opt)}>
                <span>{opt}</span>
                {multi && (
                  <span className={`cat-dropdown__checkbox${isChecked(opt) ? " cat-dropdown__checkbox--checked" : ""}`}>
                    {isChecked(opt) && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="10" height="10">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                )}
                {!multi && isChecked(opt) && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </li>
            ))}
          </ul>
          {multi && (
            <div className="cat-dropdown__footer">
              <button type="button" className="cat-dropdown__footer-clear"
                onClick={() => onChange([])} disabled={selected.length === 0}>Clear all</button>
              <button type="button" className="cat-dropdown__footer-done"
                onClick={() => { setOpen(false); setSearch(""); }}>Done</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Upload helpers ───────────────────────────────────────────────────────────
const IMAGE_MAX_MB = 5;
const VIDEO_MAX_MB = 20;
const IMAGE_MAX_BYTES = IMAGE_MAX_MB * 1024 * 1024;
const VIDEO_MAX_BYTES = VIDEO_MAX_MB * 1024 * 1024;
const VIDEO_EXTS_MODAL = [".mp4", ".mov", ".avi", ".mkv", ".webm"];

function isVideoFile(f) { return VIDEO_EXTS_MODAL.some((ext) => f.name.toLowerCase().endsWith(ext)); }
function isImageFile(f) { return f.type.startsWith("image/"); }
function formatBytes(b) { return b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`; }

// ─── Upload Drawer (wide split layout) ───────────────────────────────────────
function UploadModal({ onClose, onUploaded, categories, showSnack, storage }) {
  const [files, setFiles] = useState([]);
  const [fileMeta, setFileMeta] = useState([]);   // [{ title }]
  const [activeIdx, setActiveIdx] = useState(0);
  const [category, setCategory] = useState(null);   // { id, name } | null
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [curFile, setCurFile] = useState(0);
  const inputRef = useRef();

  const previewUrls = useMemo(() => files.map(f => URL.createObjectURL(f)), [files]);

  const validateAndAdd = (newFiles) => {
    const errors = []; const valid = [];
    Array.from(newFiles).forEach((f) => {
      if (!isImageFile(f) && !isVideoFile(f)) { errors.push(`"${f.name}" is not an image or video.`); return; }
      if (isImageFile(f) && f.size > IMAGE_MAX_BYTES) { errors.push(`"${f.name}" exceeds ${IMAGE_MAX_MB} MB.`); return; }
      if (isVideoFile(f) && f.size > VIDEO_MAX_BYTES) { errors.push(`"${f.name}" exceeds ${VIDEO_MAX_MB} MB.`); return; }
      valid.push(f);
    });
    if (errors.length) { showSnack(errors.join(" ")); return; }
    setFiles((prev) => { setActiveIdx(prev.length); return [...prev, ...valid]; });
    setFileMeta((prev) => [
      ...prev,
      ...valid.map((f) => ({ title: f.name.replace(/\.[^.]+$/, "") })),
    ]);
  };

  const removeFile = (idx) => {
    setFiles((p) => p.filter((_, i) => i !== idx));
    setFileMeta((p) => p.filter((_, i) => i !== idx));
    setActiveIdx((p) => Math.max(0, p >= idx ? p - 1 : p));
  };

  const updateMeta = (idx, field, val) =>
    setFileMeta((p) => p.map((m, i) => i === idx ? { ...m, [field]: val } : m));

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false); validateAndAdd(e.dataTransfer.files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) { showSnack("Please select at least one file."); return; }
    if (!category) { showSnack("Please select a category."); return; }

    // Client-side quota pre-check
    if (storage) {
      const incomingBytes = files.reduce((sum, f) => sum + f.size, 0);
      if (storage.used_bytes + incomingBytes > storage.total_bytes) {
        const remainMB = ((storage.total_bytes - storage.used_bytes) / (1024 * 1024)).toFixed(0);
        showSnack(`Not enough storage. You only have ${remainMB} MB remaining.`);
        return;
      }
    }
    setUploading(true); setProgress(0);
    try {
      const token = localStorage.getItem("admin_token");
      for (let i = 0; i < files.length; i++) {
        setCurFile(i);
        const m = fileMeta[i] || {};
        const fd = new FormData();
        fd.append("file", files[i]);
        fd.append("folder", category.name);
        fd.append("category_id", category.id);
        fd.append("title", m.title || files[i].name.replace(/\.[^.]+$/, ""));
        fd.append("ref_id", category.name || "uploads");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/gallery/`, {
          method: "POST", headers: { Authorization: `Bearer ${token}`, OrgId: "resh-dev" }, body: fd,
        });
        const data = await res.json();
        if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Upload failed");
        setProgress(Math.round(((i + 1) / files.length) * 100));
      }
      onUploaded(); onClose();
    } catch (err) { showSnack(err.message); }
    finally { setUploading(false); }
  };

  const activeMeta = fileMeta[activeIdx] || { title: "" };

  return (
    <div className="upload-drawer__overlay" role="dialog" aria-modal="true">
      <div className="upload-drawer upload-drawer--wide">

        {/* ── Header ── */}
        <div className="upload-drawer__head">
          <div className="upload-drawer__head-text">
            <h3 className="upload-drawer__title">Upload Media</h3>
            <p className="upload-drawer__subtitle">
              {files.length > 0
                ? `${files.length} file${files.length > 1 ? "s" : ""} queued — click a thumbnail to edit its details`
                : "Add photos & videos to your gallery"}
            </p>
          </div>
          <button type="button" className="upload-drawer__close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Split body ── */}
        <div className="upload-drawer__split">

          {/* ═══ LEFT — file list ═══ */}
          <div className="upload-drawer__left">

            {/* Drop zone */}
            <div
              className={`upload-drawer__dropzone${dragging ? " upload-drawer__dropzone--active" : ""}`}
              onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
              onClick={() => inputRef.current?.click()}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ opacity: 0.4 }}>
                <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
              </svg>
              <p className="upload-drawer__dropzone-text">
                Drop files or <span className="upload-drawer__dropzone-link">browse</span>
              </p>
              <p className="upload-drawer__dropzone-hint">
                Max file size: Images up to {IMAGE_MAX_MB} MB • Videos up to {VIDEO_MAX_MB} MB
              </p>
            </div>
            <input ref={inputRef} type="file" accept="image/*,video/*" multiple style={{ display: "none" }}
              onChange={(e) => validateAndAdd(e.target.files)} />

            {/* File count badge */}
            {files.length > 0 && (
              <div className="upload-drawer__left-header">
                <span className="upload-drawer__left-count">{files.length} file{files.length > 1 ? "s" : ""}</span>
                {!uploading && (
                  <button type="button" className="upload-drawer__left-clear"
                    onClick={() => { setFiles([]); setFileMeta([]); setActiveIdx(0); }}>
                    Clear all
                  </button>
                )}
              </div>
            )}

            {/* Thumbnail list */}
            <div className="upload-drawer__thumb-list">
              {files.map((f, i) => (
                <div
                  key={i}
                  className={`upload-drawer__thumb${activeIdx === i ? " upload-drawer__thumb--active" : ""}`}
                  onClick={() => setActiveIdx(i)}
                >
                  {/* Thumbnail */}
                  <div className="upload-drawer__thumb-media">
                    {isVideoFile(f) ? (
                      <div className="upload-drawer__thumb-video">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="18">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      </div>
                    ) : (
                      <img src={previewUrls[i]} alt={f.name} className="upload-drawer__thumb-img" />
                    )}
                    {/* Uploading overlay */}
                    {uploading && curFile === i && (
                      <div className="upload-drawer__thumb-uploading">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width="18" height="18"
                          className="upload-drawer__thumb-spinner">
                          <circle cx="12" cy="12" r="9" strokeDasharray="56" strokeDashoffset="14" />
                        </svg>
                      </div>
                    )}
                    {/* Done overlay */}
                    {uploading && curFile > i && (
                      <div className="upload-drawer__thumb-done">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" width="14" height="14">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="upload-drawer__thumb-info">
                    <span className="upload-drawer__thumb-name">
                      {(fileMeta[i]?.title || f.name.replace(/\.[^.]+$/, "")).slice(0, 24)}
                    </span>
                    <span className="upload-drawer__thumb-size">
                      {formatBytes(f.size)}
                    </span>
                  </div>

                  {/* Remove */}
                  {!uploading && (
                    <button type="button" className="upload-drawer__thumb-remove"
                      onClick={(e) => { e.stopPropagation(); removeFile(i); }} aria-label="Remove">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="10" height="10">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ═══ RIGHT — form ═══ */}
          <div className="upload-drawer__right">
            <form id="upload-form" onSubmit={handleSubmit}>

              {/* ── SHARED SECTION ── */}
              <div className="upload-drawer__section">


                <div className="upload-drawer__field">
                  <label className="upload-drawer__label">
                    Category <span className="upload-modal__required">*</span>
                  </label>
                  <CategoryDropdown
                    categories={categories}
                    value={category?.name || ""}
                    onChange={(name) => {
                      if (name === "All") {
                        setCategory({ id: "All", name: "All" });
                      } else {
                        const found = categories.find((c) => c.name === name);
                        setCategory(found ? { id: found._id, name: found.name } : null);
                      }
                    }}
                    placeholder="Select a category"
                    showAll={true}
                  />
                </div>
              </div>

              {/* ── PER-FILE SECTION ── */}
              {files.length === 0 ? (
                <div className="upload-drawer__empty-state">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.7" style={{ opacity: 0.15 }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                  </svg>
                  <p className="upload-drawer__empty-title">No files selected</p>
                  <p className="upload-drawer__empty-hint">Add files on the left — each file gets its own title.</p>
                </div>
              ) : (
                <div className="upload-drawer__section upload-drawer__section--file">


                  {/* Big preview */}
                  <div className="upload-drawer__big-preview">
                    {isVideoFile(files[activeIdx]) ? (
                      <video key={activeIdx} src={previewUrls[activeIdx]}
                        className="upload-drawer__big-media" controls muted />
                    ) : (
                      <img key={activeIdx} src={previewUrls[activeIdx]}
                        alt={files[activeIdx]?.name} className="upload-drawer__big-media" />
                    )}

                    {/* Prev / Next overlay arrows */}
                    {files.length > 1 && (
                      <>
                        <button type="button" className="upload-drawer__preview-arrow upload-drawer__preview-arrow--left"
                          onClick={() => setActiveIdx((p) => Math.max(0, p - 1))} disabled={activeIdx === 0}
                          aria-label="Previous file">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                            <polyline points="15 18 9 12 15 6" />
                          </svg>
                        </button>
                        <button type="button" className="upload-drawer__preview-arrow upload-drawer__preview-arrow--right"
                          onClick={() => setActiveIdx((p) => Math.min(files.length - 1, p + 1))}
                          disabled={activeIdx === files.length - 1} aria-label="Next file">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>

                  {/* Per-file fields */}
                  <div className="upload-drawer__fields">
                    <div className="upload-drawer__field">
                      <label className="upload-drawer__label">Title</label>
                      <input className="upload-drawer__input" type="text"
                        placeholder="e.g. Priya & Arjun Wedding"
                        value={activeMeta.title}
                        onChange={(e) => updateMeta(activeIdx, "title", e.target.value)} />
                    </div>
                  </div>


                </div>
              )}

              {/* Progress */}
              {uploading && (
                <div className="upload-drawer__progress-wrap">
                  <div className="upload-drawer__progress">
                    <div className="upload-drawer__progress-bar" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="upload-drawer__progress-label">{progress}%</span>
                </div>
              )}

            </form>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="upload-drawer__footer">
          <span className="upload-drawer__footer-count">
            {files.length > 0
              ? `${files.length} file${files.length > 1 ? "s" : ""} ready to upload`
              : "No files selected"}
          </span>
          <div className="upload-drawer__footer-actions">
            <button type="button" className="upload-drawer__btn upload-drawer__btn--cancel"
              onClick={onClose} disabled={uploading}>
              Cancel
            </button>
            <button type="submit" form="upload-form" className="upload-drawer__btn upload-drawer__btn--submit"
              disabled={uploading || !files.length}>
              {uploading
                ? `Uploading ${curFile + 1} of ${files.length}…`
                : `Upload${files.length > 0 ? ` (${files.length})` : ""}`}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Edit Modal ──────────────────────────────────────────────────────────────
function EditModal({ image, onClose, onUpdated, showSnack }) {
  const [title, setTitle] = useState(image.title || "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { showSnack("Title is required."); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      const itemId = image._id || image.id;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/gallery/${itemId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, OrgId: "resh-dev" },
        body: JSON.stringify({ title: title.trim(), description: "", tags: [] }),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Update failed");
      onUpdated(data.data);
      showSnack("Changes saved.", "success");
      onClose();
    } catch (err) { showSnack(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="edit-popup__overlay" role="dialog" aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="edit-popup">

        {/* Header */}
        <div className="edit-popup__head">
          <h3 className="edit-popup__title">Edit Title</h3>
          <button type="button" className="edit-popup__close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Thumbnail */}
        <div className="edit-popup__thumb">
          {isVideoUrl(image.url || "") ? (
            <video src={image.url} className="edit-popup__thumb-media" muted />
          ) : (
            <img src={image.url} alt={image.title} className="edit-popup__thumb-media" />
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="edit-popup__form">
          <label className="edit-popup__label" htmlFor="edit-title">
            Title <span style={{ color: "#c0392b" }}>*</span>
          </label>
          <input
            id="edit-title"
            className="edit-popup__input"
            type="text"
            placeholder="e.g. Priya & Arjun Wedding"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />

          <div className="edit-popup__actions">
            <button type="button" className="upload-drawer__btn upload-drawer__btn--cancel"
              onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="upload-drawer__btn upload-drawer__btn--submit"
              disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
function DeleteModal({ image, onClose, onDeleted, showSnack }) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const itemId = image._id || image.id;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/gallery/${itemId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}`, OrgId: "resh-dev" },
      });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Delete failed");
      onDeleted(itemId); onClose();
    } catch (err) { showSnack(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="delete-modal__overlay" role="dialog" aria-modal="true">
      <div className="delete-modal">
        <h3 className="delete-modal__title">Delete Media?</h3>
        <p className="delete-modal__msg">
          "{image.title || image.file_name}" will be permanently deleted. This cannot be undone.
        </p>
        <div className="delete-modal__actions">
          <button className="delete-modal__btn delete-modal__btn--cancel" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="delete-modal__btn delete-modal__btn--confirm" onClick={handleDelete} disabled={loading}>
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Gallery Card ────────────────────────────────────────────────────────────
const VIDEO_EXTS = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
const isVideoUrl = (url = "") => VIDEO_EXTS.some((ext) => url.toLowerCase().includes(ext));

function GalleryCard({ image, onEdit, onDelete }) {
  const video = isVideoUrl(image.url || image.storage_name || "");
  return (
    <div className="gallery-card">
      <div className="gallery-card__img-wrap">
        {video ? (
          <>
            <video className="gallery-card__img" src={image.url} muted preload="metadata" />
            <span className="gallery-card__type-badge gallery-card__type-badge--video">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Video
            </span>
          </>
        ) : (
          <>
            <img className="gallery-card__img" src={image.url} alt={image.title || image.file_name} loading="lazy" />
            <span className="gallery-card__type-badge gallery-card__type-badge--image">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
              </svg>
              Photo
            </span>
          </>
        )}
        <div className="gallery-card__overlay">
          <button className="gallery-card__action gallery-card__action--edit"
            onClick={() => onEdit(image)}>Edit</button>
          <button className="gallery-card__action gallery-card__action--delete"
            onClick={() => onDelete(image)}>Delete</button>
        </div>
      </div>
      <div className="gallery-card__info">
        <p className="gallery-card__name">{image.title || image.file_name}</p>
        <p className="gallery-card__meta">{image.category}</p>
      </div>
    </div>
  );
}

// ─── Pagination Controls ──────────────────────────────────────────────────────
const PAGE_SIZE = Number(process.env.REACT_APP_PAGE_SIZE) || 25;

function PaginationControls({ currentPage, totalPages, totalDocs, pageSize, onPageChange, loading }) {
  if (totalDocs <= pageSize) return null;

  const startDoc = (currentPage - 1) * pageSize + 1;
  const endDoc = Math.min(currentPage * pageSize, totalDocs);

  const pages = [];
  const delta = 2;
  const left = Math.max(1, currentPage - delta);
  const right = Math.min(totalPages, currentPage + delta);
  for (let i = left; i <= right; i++) pages.push(i);

  return (
    <div className="bookings-pagination">
      <span className="bookings-pagination__info">
        Showing {startDoc}–{endDoc} of {totalDocs} media items
      </span>
      <div className="bookings-pagination__controls">
        <button
          className="bookings-pagination__btn"
          disabled={currentPage === 1 || loading}
          onClick={() => onPageChange(1)}
          title="First page"
        >
          «
        </button>
        <button
          className="bookings-pagination__btn"
          disabled={currentPage === 1 || loading}
          onClick={() => onPageChange(currentPage - 1)}
          title="Previous page"
        >
          ‹
        </button>

        {left > 1 && (
          <>
            <button className="bookings-pagination__btn" onClick={() => onPageChange(1)}>1</button>
            {left > 2 && <span className="bookings-pagination__ellipsis">…</span>}
          </>
        )}

        {pages.map((p) => (
          <button
            key={p}
            className={`bookings-pagination__btn ${p === currentPage ? "bookings-pagination__btn--active" : ""}`}
            disabled={loading}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        ))}

        {right < totalPages && (
          <>
            {right < totalPages - 1 && <span className="bookings-pagination__ellipsis">…</span>}
            <button className="bookings-pagination__btn" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
          </>
        )}

        <button
          className="bookings-pagination__btn"
          disabled={currentPage === totalPages || loading}
          onClick={() => onPageChange(currentPage + 1)}
          title="Next page"
        >
          ›
        </button>
        <button
          className="bookings-pagination__btn"
          disabled={currentPage === totalPages || loading}
          onClick={() => onPageChange(totalPages)}
          title="Last page"
        >
          »
        </button>
      </div>
    </div>
  );
}

// ─── Gallery Page ─────────────────────────────────────────────────────────────
export default function Gallery() {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaType, setMediaType] = useState("All");
  const [activeCategory, setActiveCategory] = useState("All");
  const [showUpload, setShowUpload] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [storage, setStorage] = useState(null); // StorageStats

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);

  const { snack, showSnack, dismissSnack } = useSnackbar();

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/entities/filter/function_types`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, OrgId: "resh-dev" },
        body: JSON.stringify({ sort: [{ colId: "name", sort: "asc" }] }),
      });
      const data = await res.json();
      if (res.ok && data.status === 200) setCategories(data.data?.[0]?.response ?? []);
    } catch (_) { }
  }, []);

  const fetchStorage = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/gallery/storage`, {
        headers: { Authorization: `Bearer ${token}`, OrgId: "resh-dev" },
      });
      const data = await res.json();
      if (res.ok && data.status === 200) setStorage(data.data);
    } catch (_) { }
  }, []);

  const buildFilterConditions = useCallback(() => {
    const conditions = [];

    // Filter by Media Type (Image or Video)
    if (mediaType !== "All") {
      conditions.push({
        clause: "AND",
        conditions: [
          { column: "media_type", operator: "EQUALS", type: "string", value: mediaType.toLowerCase() }
        ]
      });
    }

    // Filter by Category
    if (activeCategory !== "All") {
      conditions.push({
        clause: "AND",
        conditions: [
          { column: "category", operator: "EQUALS", type: "string", value: activeCategory }
        ]
      });
    }

    return conditions;
  }, [mediaType, activeCategory]);

  const fetchGallery = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const start = (page - 1) * PAGE_SIZE;
      const end = page * PAGE_SIZE;

      const filterConditions = buildFilterConditions();

      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/gallery/list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          OrgId: "resh-dev"
        },
        body: JSON.stringify({
          start,
          end,
          filter: filterConditions,
          sort: [{ colId: "created_at", sort: "desc" }],
          first_filter_append: true
        })
      });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Failed to load gallery");

      const responseArr = data?.data?.[0]?.response ?? [];
      const paginationArr = data?.data?.[0]?.pagination ?? [];
      const total = paginationArr?.[0]?.totalDocs ?? 0;

      setImages(responseArr);
      setTotalDocs(total);
    } catch (err) {
      showSnack(err.message);
    } finally {
      setLoading(false);
    }
  }, [buildFilterConditions, showSnack]);

  const refreshAll = useCallback(() => {
    fetchGallery(currentPage);
    fetchStorage();
  }, [fetchGallery, fetchStorage, currentPage]);

  useEffect(() => {
    fetchCategories();
    fetchStorage();
  }, [fetchCategories, fetchStorage]);

  useEffect(() => {
    fetchGallery(currentPage);
  }, [fetchGallery, currentPage, mediaType, activeCategory]);

  const handleMediaTypeChange = (type) => {
    setMediaType(type);
    setCurrentPage(1);
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  const handleDeleted = () => refreshAll();
  const handleUpdated = () => refreshAll();

  return (
    <AdminLayout>
      <div className="gallery-page__header">
        <h2 className="gallery-page__title">Gallery</h2>
        <div className="gallery-page__header-right">
          <StorageBar stats={storage} />
          <button
            className="gallery-page__upload-btn"
            onClick={() => {
              if (storage && storage.percent >= 100) {
                showSnack("Storage limit reached. Cannot upload more files.");
                return;
              }
              setShowUpload(true);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Upload Media
          </button>
        </div>
      </div>

      <div className="gallery-page__filter-bar">
        <div className="gallery-page__media-pills">
          {["All", "Image", "Video"].map((type) => (
            <button key={type}
              className={`gallery-page__media-pill${mediaType === type ? " gallery-page__media-pill--active" : ""}`}
              onClick={() => handleMediaTypeChange(type)}>
              {type === "Image" && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>}
              {type === "Video" && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13"><polygon points="5 3 19 12 5 21 5 3" /></svg>}
              {type}
            </button>
          ))}
        </div>
        <div className="gallery-page__filter-divider" />
        <CategoryDropdown categories={categories} value={activeCategory} onChange={handleCategoryChange} />
      </div>

      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div className="app-loading-overlay">
            <div className="app-spinner"></div>
          </div>
        )}
        <div className={`gallery-page__grid ${loading ? "gallery-page__grid--loading" : ""}`}>
          {images.length === 0 && !loading ? (
            <p className="gallery-page__empty">No media found for the selected filters.</p>
          ) : images.map((img) => (
            <GalleryCard key={img._id || img.id} image={img} onEdit={setEditTarget} onDelete={setDeleteTarget} />
          ))}
        </div>
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={Math.max(1, Math.ceil(totalDocs / PAGE_SIZE))}
        totalDocs={totalDocs}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
        loading={loading}
      />


      {showUpload && (
        <UploadModal
          categories={categories}

          onClose={() => setShowUpload(false)}
          onUploaded={refreshAll}
          showSnack={showSnack}
          storage={storage}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          image={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
          showSnack={showSnack}
        />
      )}
      {editTarget && (
        <EditModal
          image={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={handleUpdated}
          showSnack={showSnack}
        />
      )}

      <Snackbar snack={snack} onDismiss={dismissSnack} />
    </AdminLayout>
  );
}
